import { Activity, useState } from 'react'
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
} from '@mui/material'

import ArrowDropDownTwoTone from '@mui/icons-material/ArrowDropDownTwoTone'
import { useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

const MenuItem = ({
  id,
  text,
  Icon,
  subItems = [],
  onClick,
  isSubItem,
  isCollaped,
}) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const location = useLocation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const isActive = (itemId, isSubItem) => {
    if (isSubItem) {
      return location.pathname === `/${itemId}`
    }
    return (
      location.pathname === `/${itemId}` ||
      location.pathname.startsWith(`/${itemId}/`)
    )
  }

  const active = isActive(id, isSubItem)

  const handleItemClick = (itemId, hasSubItems) => {
    if (hasSubItems) {
      setOpen(!open)
    } else {
      onClick(itemId)
    }
  }

  const menuItem = (
    <ListItemButton
      onClick={() => handleItemClick(id, subItems.length > 0)}
      aria-label={text}
      sx={{
        backgroundColor: active
          ? theme.palette.menu.background.selected
          : 'transparent',
        color: active
          ? theme.palette.menu.text.selected
          : theme.palette.menu.text.default,
        '&:hover': {
          backgroundColor: active
            ? theme.palette.menu.background.selected
            : theme.palette.action.hover,
        },
        '&.Mui-focusVisible': {
          backgroundColor: active
            ? theme.palette.menu.background.selected
            : theme.palette.action.focus,
          color: active
            ? theme.palette.menu.text.selected
            : theme.palette.menu.text.default,
          outline: `2px solid ${theme.palette.menu.text.default}`,
          '& .MuiListItemIcon-root': {
            color: active
              ? theme.palette.menu.text.selected
              : theme.palette.menu.text.default,
          },
        },
        padding: '8px 12px',
        minHeight: isMobile ? '40px' : '48px',
        margin: `${theme.spacing(1)} ${theme.spacing(1)}`,
        marginTop: `0px`,
        borderRadius: `${theme.shape.borderRadius}px`,
        transition: theme.transitions.create(['width', 'padding'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isMobile ? 30 : 35,
          color: active
            ? theme.palette.menu.text.selected
            : theme.palette.menu.text.default,
        }}
      >
        <Icon fontSize={isMobile ? 'small' : 'medium'} />
      </ListItemIcon>

      {/* For very small screens, conditionally show text */}
      {(!isMobile || isTablet) && (
        <ListItemText
          primary={text}
          primaryTypographyProps={{
            fontSize: isMobile ? '0.875rem' : '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        />
      )}

      <Activity mode={subItems.length > 0 ? 'visible' : 'hidden'}>
        <ArrowDropDownTwoTone
          sx={{
            transform: open ? 'rotate(-180deg)' : 'rotate(0)',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
          }}
        />
      </Activity>
    </ListItemButton>
  )

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      {isMobile && !isTablet ? (
        <Tooltip title={text} placement="right">
          {menuItem}
        </Tooltip>
      ) : (
        menuItem
      )}

      {subItems.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems.map((subItem) => (
              <ListItemButton
                key={subItem.id}
                aria-label={text}
                sx={{
                  pl: isMobile ? 3 : 4,
                  boxShadow: isActive(subItem.id, true)
                    ? `inset 4px 4px 0 0 ${theme.palette.primary.main}`
                    : 'none',
                  backgroundColor: isActive(subItem.id, true)
                    ? theme.palette.action.selected
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: isActive(subItem.id, true)
                      ? theme.palette.action.selected
                      : theme.palette.action.focus,
                  },
                  minHeight: isMobile ? '36px' : '40px',
                }}
                onClick={() => handleItemClick(subItem.id, false)}
              >
                <ListItemIcon sx={{ minWidth: isMobile ? 28 : 35 }}>
                  <subItem.Icon fontSize={isMobile ? 'small' : 'medium'} />
                </ListItemIcon>
                {(!isMobile || isTablet) && (
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                    }}
                  />
                )}
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  )
}

export default MenuItem
